// /app/frontend/src/pages/DashboardPage.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth } from "../contexts/AuthContext";
import { dashboardAPI } from "../services/api";
import { JURISDICTIONS } from "../constants/compliance";
import {
  AlertTriangle,
  Shield,
  Users,
  CheckCircle,
  Clock,
  Building2,
  FileWarning,
  UserCheck,
  Briefcase,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Target,
  ChevronRight,
  AlertCircle,
  Bell,
  Activity,
  BarChart3,
  Loader2,
} from "lucide-react";

// Metric Card Component (for DPO Dashboard bento grid layout)
function MetricCard({
  title,
  value,
  subtitle,
  subtitleDetail,
  icon: Icon,
  color = "primary",
  onClick,
  borderColor = null,
  trend,
  trendValue,
}) {
  const colorClasses = {
    primary: "bg-slate-500/10 text-slate-600 dark:text-slate-400",
    destructive: "bg-rose-500/10 text-rose-600 dark:text-rose-400",
    warning: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    success: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    purple: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
    blue: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  };

  const borderColorMap = {
    destructive: "#ef4444",
    warning: "#f59e0b",
    success: "#10b981",
    purple: "#a855f7",
    blue: "#3b82f6",
    primary: "#64748b",
  };

  const textColorMap = {
    destructive: "#ef4444",
    warning: "#f59e0b",
    success: "#10b981",
    purple: "#a855f7",
    blue: "#3b82f6",
    primary: "#64748b",
  };

  return (
    <div
      className={`
        relative transition-all duration-300 ease-out rounded-xl
        hover:shadow-lg hover:-translate-y-1
        ${onClick ? "cursor-pointer" : ""}
        bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm
        p-4
      `}
      onClick={onClick}
      style={{
        borderLeft: `4px solid ${borderColorMap[borderColor || color]}`,
        borderTop: "1px solid rgba(148, 163, 184, 0.3)",
        borderRight: "1px solid rgba(148, 163, 184, 0.3)",
        borderBottom: "1px solid rgba(148, 163, 184, 0.3)",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
      }}
    >
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-slate-100/50 dark:to-slate-800/30 pointer-events-none" />

      <div className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-1 flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold tracking-tight">{value}</p>
            {subtitle && (
              <p className="text-xs font-medium" style={{ color: textColorMap[color] }}>
                {subtitle}
              </p>
            )}
            {subtitleDetail && <p className="text-xs text-muted-foreground">{subtitleDetail}</p>}
            {trendValue && (
              <p className="text-xs text-muted-foreground">
                {trend === "up" ? "↑" : "→"} {trendValue}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]} flex-shrink-0 ml-2`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </div>
    </div>
  );
}

// Module Status Card - Per Spec 4.3 (Active Breaches, Pending DSRs, ROPA, Vendor Risk)
function ModuleStatusCard({ title, value, subtitle, ctaText, ctaLink, icon: Icon, borderColor = "primary" }) {
  const borderColorMap = {
    primary: "#64748b",
    red: "#ef4444",
    warning: "#f59e0b",
    success: "#10b981",
    green: "#10b981",
  };

  const textColorClasses = {
    red: "text-red-500",
    warning: "text-amber-500",
    success: "text-emerald-500",
    green: "text-emerald-500",
  };

  return (
    <div
      style={{
        position: "relative",
        borderRadius: "0.75rem",
        overflow: "hidden",
        border: "1px solid rgba(148, 163, 184, 0.3)",
        background: "rgba(15, 23, 42, 0.4)",
        boxShadow: "0 1px 2px rgba(148, 163, 184, 0.1)",
      }}
    >
      {/* Color accent - Left side bar */}
      <div
        style={{
          position: "absolute",
          left: "0",
          top: "0",
          bottom: "0",
          width: "4px",
          backgroundColor: borderColorMap[borderColor],
          zIndex: 1,
        }}
      />

      {/* Content */}
      <div style={{ paddingLeft: "1rem", paddingRight: "1rem", paddingTop: "1.5rem", paddingBottom: "1rem" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "0.75rem",
          }}
        >
          <div>
            <p style={{ fontSize: "0.875rem", color: "#9ca3af", marginBottom: "0.5rem" }}>{title}</p>
            <p style={{ fontSize: "2.25rem", fontWeight: "bold" }}>{value}</p>
          </div>
          <Icon
            style={{ width: "1.5rem", height: "1.5rem" }}
            className={textColorClasses[borderColor] || "text-slate-400"}
          />
        </div>

        {subtitle && (
          <p
            style={{ fontSize: "0.875rem", marginBottom: "0.75rem" }}
            className={textColorClasses[borderColor] || "text-muted-foreground"}
          >
            {subtitle}
          </p>
        )}

        {ctaText && ctaLink && (
          <Link
            to={ctaLink}
            style={{ fontSize: "0.875rem", color: "#10b981", textDecoration: "none", fontWeight: "500" }}
          >
            {ctaText} →
          </Link>
        )}
      </div>
    </div>
  );
}

// Alert Banner Component
function AlertBanner({ alerts }) {
  // Handle single alert object (from backend) or array of alerts
  const alertArray = !alerts ? [] : Array.isArray(alerts) ? alerts : [alerts];
  if (alertArray.length === 0 || !alertArray[0]) return null;

  const criticalAlerts = alertArray.filter((a) => a?.severity === "critical");
  const warningAlerts = alertArray.filter((a) => a?.severity === "warning");

  return (
    <Card
      className={`border-l-4 ${criticalAlerts.length > 0 ? "border-l-red-500 bg-red-500/5" : "border-l-amber-500 bg-amber-500/5"}`}
    >
      <CardContent className="py-4">
        <div className="flex items-start gap-4">
          <div className={`p-2 rounded-full ${criticalAlerts.length > 0 ? "bg-red-500/10" : "bg-amber-500/10"}`}>
            <Bell className={`h-5 w-5 ${criticalAlerts.length > 0 ? "text-red-500" : "text-amber-500"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold">
                {criticalAlerts.length > 0
                  ? `${criticalAlerts.length} Critical Alert${criticalAlerts.length > 1 ? "s" : ""}`
                  : `${warningAlerts.length} Warning${warningAlerts.length > 1 ? "s" : ""}`}
              </h3>
              {criticalAlerts.length > 0 && warningAlerts.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  +{warningAlerts.length} warnings
                </Badge>
              )}
            </div>
            <div className="space-y-1">
              {alertArray.slice(0, 3).map(
                (alert, idx) =>
                  alert && (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <AlertCircle
                        className={`h-4 w-4 flex-shrink-0 ${alert.severity === "critical" ? "text-red-500" : "text-amber-500"}`}
                      />
                      <span className="font-medium">{alert.title}</span>
                      <span className="text-muted-foreground">—</span>
                      <span className="text-muted-foreground truncate">{alert.description}</span>
                    </div>
                  ),
              )}
              {alertArray.length > 3 && (
                <p className="text-xs text-muted-foreground mt-1">
                  +{alertArray.length - 3} more items requiring attention
                </p>
              )}
            </div>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to="/operations/tasks">View All</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Client Health Matrix Component with clickable indicators
function ClientHealthMatrix({ clients }) {
  const getStatusColor = (status) => {
    switch (status) {
      case "critical":
        return "bg-red-500";
      case "warning":
        return "bg-amber-500";
      default:
        return "bg-green-500";
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Client health matrix</CardTitle>
        <CardDescription className="text-xs">CLICK ANY ROW TO OPEN CLIENT DASHBOARD</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-[300px] overflow-y-auto">
          {clients.map((client) => (
            <div
              key={client.client_id}
              onClick={() => (window.location.href = `/dashboard?client=${client.client_id}`)}
              className="p-3 rounded-lg border border-slate-200/30 hover:border-slate-400/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
            >
              {/* Top row: Client name (left) and Badges (right) */}
              <div className="flex items-center justify-between gap-4 mb-2">
                {/* Left: Status dot + Client name */}
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getStatusColor(client.overall_status)}`} />
                  <p className="font-medium text-sm truncate">{client.client_name}</p>
                </div>

                {/* Right: Badges (no wrapping) */}
                <div className="flex gap-2 flex-shrink-0">
                  {/* ROPA - Dark red/maroon background with red text */}
                  {client.indicators?.ropa && (
                    <Badge
                      variant="outline"
                      className="text-xs bg-red-800 text-red-300 border-0 whitespace-nowrap font-semibold"
                    >
                      ROPA: {client.indicators.ropa.count}%
                    </Badge>
                  )}

                  {/* Breaches - Red if count > 0, Green if 0 */}
                  {client.indicators?.breaches && (
                    <Badge
                      variant="outline"
                      className={`text-xs border-0 whitespace-nowrap font-semibold ${
                        client.indicators.breaches.count > 0 ? "bg-red-800 text-red-300" : "bg-green-800 text-green-300"
                      }`}
                    >
                      Breaches: {client.indicators.breaches.count}
                    </Badge>
                  )}

                  {/* DSRs - Dark amber/gold background with amber text */}
                  {client.indicators?.dsrs && (
                    <Badge
                      variant="outline"
                      className="text-xs bg-amber-800 text-amber-300 border-0 whitespace-nowrap font-semibold"
                    >
                      DSRs: {client.indicators.dsrs.count}
                    </Badge>
                  )}

                  {/* Vendors - Dark amber if unreviewed, Green if clear */}
                  {client.indicators?.vendors && (
                    <Badge
                      variant="outline"
                      className={`text-xs whitespace-nowrap font-semibold border-0 ${
                        client.indicators.vendors.status === "warning"
                          ? "bg-amber-800 text-amber-300"
                          : "bg-green-800 text-green-300"
                      }`}
                    >
                      Vendors:{" "}
                      {client.indicators.vendors.status === "warning"
                        ? `${client.indicators.vendors.count} unreviewed`
                        : "clear"}
                    </Badge>
                  )}

                  {/* Maturity - Colored text based on level */}
                  {client.maturity_score > 0 && (
                    <Badge
                      variant="outline"
                      className={`text-xs border-0 whitespace-nowrap font-semibold ${
                        client.maturity_level === "Reactive"
                          ? "bg-transparent text-red-400"
                          : client.maturity_level === "Foundational"
                            ? "bg-transparent text-blue-400"
                            : client.maturity_level === "Provisional"
                              ? "bg-transparent text-amber-400"
                              : client.maturity_level === "Defined"
                                ? "bg-transparent text-blue-400"
                                : "bg-transparent text-emerald-400"
                      }`}
                    >
                      {client.maturity_level || "Reactive"}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Bottom row: Details (entity count • jurisdiction • sector) */}
              <p className="text-xs text-muted-foreground">
                {client.entity_count} {client.entity_count === 1 ? "entity" : "entities"} • {client.jurisdiction || "Unknown"} •{" "}
                {client.industry || "Unknown"}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Upcoming Deadlines Component
function UpcomingDeadlines({ deadlines }) {
  const getDeadlineColor = (daysUntil) => {
    if (daysUntil <= 2) return "text-red-500";
    if (daysUntil <= 5) return "text-amber-500";
    if (daysUntil <= 14) return "text-green-500";
    return "text-muted-foreground";
  };

  const getJurisdictionLabel = (item) => {
    if (item.type === "dsr" && item.jurisdiction) {
      const juris = JURISDICTIONS.find(j => j.value === item.jurisdiction);
      if (juris) {
        const limit = juris.deadlineDays === 45 ? "45-day limit" : `${juris.deadlineDays}-day limit`;
        return `${juris.label} DPA • ${limit}`;
      }
    }
    if (item.type === "breach") {
      return "Breach notification • 72-hr window";
    }
    if (item.type === "dpia") {
      return "DPIA review • 12-month periodic";
    }
    if (item.type === "task") {
      return "Task deadline";
    }
    return "Review deadline";
  };

  return (
    <Card>
      <CardHeader className="pb-4 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg">Upcoming deadlines</CardTitle>
        <a href="#" className="text-sm text-green-500 hover:text-green-600 font-medium">
          View all →
        </a>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[280px]">
          <div className="space-y-4 pr-4">
            {deadlines.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No upcoming deadlines</p>
              </div>
            ) : (
              deadlines.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start justify-between gap-4 pb-4 border-b last:border-b-0 hover:bg-muted/30 -mx-2 px-2 py-2 rounded transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground leading-tight">
                      {item.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {getJurisdictionLabel(item)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-bold ${getDeadlineColor(item.days_until)}`}>
                      {item.days_until === 0
                        ? "Today"
                        : item.days_until === 1
                          ? "1 day"
                          : `${item.days_until} days`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {new Date(item.deadline).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric"
                      })}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

// Weekly Trends Chart (simple bar representation)
function WeeklyTrends({ data }) {
  if (!data || data.length === 0) return null;

  const maxValue = Math.max(...data.map((d) => Math.max(d.breaches, d.dsrs, d.tasks_completed)), 1);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Weekly Activity
        </CardTitle>
        <CardDescription>Last 7 days</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-red-500" />
              <span>Breaches</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-purple-500" />
              <span>DSRs</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-green-500" />
              <span>Tasks Completed</span>
            </div>
          </div>
          <div className="flex items-end justify-between gap-2 h-32">
            {data.map((day, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                <div className="flex-1 flex items-end gap-0.5 w-full">
                  <div
                    className="flex-1 bg-red-500 rounded-t transition-all"
                    style={{ height: `${(day.breaches / maxValue) * 100}%`, minHeight: day.breaches > 0 ? "4px" : "0" }}
                  />
                  <div
                    className="flex-1 bg-purple-500 rounded-t transition-all"
                    style={{ height: `${(day.dsrs / maxValue) * 100}%`, minHeight: day.dsrs > 0 ? "4px" : "0" }}
                  />
                  <div
                    className="flex-1 bg-green-500 rounded-t transition-all"
                    style={{
                      height: `${(day.tasks_completed / maxValue) * 100}%`,
                      minHeight: day.tasks_completed > 0 ? "4px" : "0",
                    }}
                  />
                </div>
                <span className="text-xs text-muted-foreground">{day.day}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main DPO Dashboard Component
function DPODashboard() {
  const { user, isComplianceReadOnly } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getMasterControl();
      setData(response.data);
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-fade-in">
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-6">
          <Skeleton className="h-96" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <p className="text-lg font-medium">{error}</p>
            <Button onClick={loadDashboardData} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const {
    metrics,
    critical_alerts,
    client_health,
    upcoming_deadlines,
    jurisdiction_breakdown,
    recent_activity,
  } = data || {};

  return (
    <div className="p-6 space-y-6" data-testid="dpo-dashboard">
      {/* Page Header */}
      <div className="flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-7 w-7 text-teal-600" />
            Master Control Center
          </h1>
          <p className="text-muted-foreground text-sm">
            Week overview • {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} • {user?.dpo_org_name || "DPO Admin"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="btn-lift"
            onClick={() => {
              // Export portfolio report - generates and downloads PDF/CSV
              window.location.href = "/dashboard/export-portfolio";
            }}
          >
            Export portfolio report
          </Button>
          {!isComplianceReadOnly && (
            <Button
              variant="default"
              size="sm"
              className="btn-lift"
              onClick={() => {
                // Log incident - navigate to breach creation form
                window.location.href = "/compliance/breaches/workflow/new";
              }}
            >
              + Log incident
            </Button>
          )}
        </div>
      </div>

      {/* Critical Alerts Banner */}
      {critical_alerts && critical_alerts.length > 0 && (
        <div className="animate-fade-in-up">
          <AlertBanner alerts={critical_alerts} />
        </div>
      )}

      {/* Bento Grid Layout */}
      <div className="bento-grid">
        {/* Key Metrics - Top Row */}
        <div className="bento-sm animate-fade-in-up stagger-1">
          <MetricCard
            title="Active Breaches"
            value={metrics?.breaches?.active || 0}
            subtitle={`${metrics?.breaches?.within_72h || 0} within 72h`}
            subtitleDetail={`${metrics?.breaches?.critical || 0} critical • +${metrics?.breaches?.this_week || 0} this week`}
            icon={FileWarning}
            color={metrics?.breaches?.within_72h > 0 ? "destructive" : "warning"}
            onClick={() => (window.location.href = "/compliance/breaches")}
          />
        </div>
        <div className="bento-sm animate-fade-in-up stagger-2">
          <MetricCard
            title="Pending DSRs"
            value={metrics?.dsrs?.pending || 0}
            subtitle={`${metrics?.dsrs?.overdue || 0} overdue • avg ${metrics?.dsrs?.avg_days_open?.toFixed(1) || 0} days open`}
            subtitleDetail={`Next deadline: ${metrics?.dsrs?.next_deadline ? new Date(metrics.dsrs.next_deadline).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "N/A"}`}
            icon={UserCheck}
            color={metrics?.dsrs?.overdue > 0 ? "warning" : "purple"}
            onClick={() => (window.location.href = "/compliance/dsr")}
          />
        </div>
        <div className="bento-sm animate-fade-in-up stagger-4">
          <MetricCard
            title="Active Assessments"
            value={metrics?.assessments?.total_active || 0}
            subtitle={`${metrics?.assessments?.questionnaires || 0} staff`}
            subtitleDetail={`${metrics?.assessments?.readiness || 0} readiness`}
            icon={Target}
            color="success"
            onClick={() => (window.location.href = "/compliance/questionnaires")}
          />
        </div>
        <div className="bento-sm animate-fade-in-up stagger-5">
          <MetricCard
            title="Portfolio Compliance"
            value={`${metrics?.portfolio?.compliance_percent?.toFixed(1) || 0}%`}
            subtitle={`${metrics?.portfolio?.orgs_below_50_percent || 0} orgs below 50%`}
            subtitleDetail={`Avg maturity: ${metrics?.portfolio?.avg_maturity_level || "N/A"}`}
            icon={BarChart3}
            color={
              metrics?.portfolio?.compliance_percent >= 80
                ? "success"
                : metrics?.portfolio?.compliance_percent >= 50
                  ? "warning"
                  : "destructive"
            }
            onClick={() => (window.location.href = "/compliance/ropa")}
          />
        </div>

        {/* Client Health Matrix - Large Card */}
        <div className="bento-xl animate-fade-in-up stagger-6">
          <ClientHealthMatrix clients={client_health || []} />
        </div>

        {/* Right Column - Upcoming Deadlines */}
        <div className="bento-md animate-fade-in-up stagger-7">
          <UpcomingDeadlines deadlines={upcoming_deadlines || []} />
        </div>

        {/* Portfolio by Jurisdiction - 2x2 Grid */}
        {jurisdiction_breakdown && jurisdiction_breakdown.length > 0 && (
          <div className="bento-lg animate-fade-in-up stagger-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Portfolio by Jurisdiction
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  {jurisdiction_breakdown.map((item) => (
                    <div
                      key={item.code}
                      className="p-4 rounded-lg border bg-slate-800/40 dark:bg-slate-900/40 hover:bg-slate-700/40 transition-colors cursor-pointer"
                      onClick={() => (window.location.href = `/compliance/clients?jurisdiction=${item.code}`)}
                    >
                      <p className="text-sm font-semibold text-white">{item.jurisdiction}</p>
                      <p className="text-3xl font-bold mt-2 text-white">{item.client_count}</p>
                      <p className="text-xs text-slate-300 mt-1">
                        {item.client_count === 1 ? "client" : "clients"} • {item.legislation}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Activity Feed - Single Column List */}
        <div className="bento-full animate-fade-in-up stagger-9">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Recent activity</CardTitle>
            </CardHeader>
            <CardContent>
              {recent_activity && recent_activity.length > 0 ? (
                <div className="space-y-3">
                  {recent_activity.map((activity, idx) => {
                    // Determine dot color based on activity type
                    const getDotColor = () => {
                      const type = activity.type?.toLowerCase() || "";
                      if (type === "breach") return "bg-red-500";
                      if (type === "dsr") return "bg-blue-500";
                      if (type === "assessment") return "bg-green-500";
                      if (type === "vendor") return "bg-amber-500";
                      if (type === "ropa") return "bg-blue-500";
                      return "bg-slate-500";
                    };

                    // Format timestamp
                    const formatTime = (timestamp) => {
                      if (!timestamp) return "";
                      const date = new Date(timestamp);
                      const today = new Date();
                      const yesterday = new Date(today);
                      yesterday.setDate(yesterday.getDate() - 1);

                      const isToday = date.toDateString() === today.toDateString();
                      const isYesterday = date.toDateString() === yesterday.toDateString();

                      const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

                      if (isToday) return `Today, ${time}`;
                      if (isYesterday) return `Yesterday, ${time}`;

                      const dayName = date.toLocaleDateString(undefined, { weekday: "short" });
                      return `${dayName}, ${time}`;
                    };

                    return (
                      <div key={idx} className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full ${getDotColor()} flex-shrink-0 mt-1.5`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground dark:text-white">{activity.description}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{formatTime(activity.timestamp)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No recent activity</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Client Portal Dashboard Component
function ClientDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardAPI.getClientControl();
      setData(response.data);
    } catch (err) {
      console.error("Failed to load client dashboard:", err);
      setError("Failed to load dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6 animate-fade-in">
        <Skeleton className="h-24 w-full" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <p className="text-lg font-medium">{error}</p>
            <Button onClick={loadDashboardData} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { client, metrics, alert, entities, upcoming_deadlines, recent_activity, maturity_assessment } = data || {};

  return (
    <div className="p-6 space-y-6" data-testid="client-dashboard">
      {/* Page Header - Per Spec 4.1 */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h1 className="text-3xl font-bold mb-2">Compliance dashboard</h1>
          <p className="text-sm text-muted-foreground">
            {client?.name} •{" "}
            {client?.entity_count
              ? `${client.entity_count} ${client.entity_count === 1 ? "entity" : "entities"}`
              : "0 entities"}{" "}
            • {client?.jurisdiction || "Unknown"} {client?.legislation ? `${client.legislation}` : ""} •{" "}
            {client?.dpo_name ? `DPO: ${client.dpo_name}` : "DPO: Unassigned"}
          </p>
        </div>
        {/* Action Buttons - Per Spec 4.1 */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <BarChart3 className="h-4 w-4 mr-2" />
            Download report
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
            + Report incident
          </Button>
        </div>
      </div>

      {/* Alert Banner - Single Highest-Priority Alert */}
      {alert && <AlertBanner alerts={alert ? [alert] : []} />}

      {/* Module Status Cards (4-column per spec 4.3) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Active Breaches - Red border if >0 */}
        <ModuleStatusCard
          title="Active Breaches"
          value={metrics?.breaches?.count || 0}
          subtitle={
            metrics?.breaches?.hours_to_deadline
              ? `Notification due in ${metrics.breaches.hours_to_deadline} hrs`
              : "None"
          }
          icon={FileWarning}
          borderColor={metrics?.breaches?.count > 0 ? "red" : "primary"}
          ctaText="View breach register"
          ctaLink="/compliance/breaches"
        />

        {/* Pending DSRs - Green border if no overdue, Amber if overdue */}
        <ModuleStatusCard
          title="Pending DSRs"
          value={metrics?.dsrs?.pending || 0}
          subtitle={metrics?.dsrs?.overdue > 0 ? `${metrics.dsrs.overdue} overdue` : "No open requests"}
          icon={UserCheck}
          borderColor={metrics?.dsrs?.overdue > 0 ? "warning" : "green"}
          ctaText="View DSR register"
          ctaLink="/compliance/dsr"
        />

        {/* ROPA Completion % - Amber border if <80%, Green if >= 80% */}
        <ModuleStatusCard
          title="ROPA Completion"
          value={`${metrics?.ropa?.percent?.toFixed(1) || 0}%`}
          subtitle={
            metrics?.ropa?.incomplete_count > 0
              ? `${metrics.ropa.incomplete_count} records incomplete`
              : "All entities complete"
          }
          icon={Shield}
          borderColor={metrics?.ropa?.percent < 80 ? "warning" : "green"}
          ctaText="Continue ROPA"
          ctaLink="/compliance/ropa"
        />

        {/* Vendor Risk - Green border if all current, Amber if overdue */}
        <ModuleStatusCard
          title="Vendor Risk"
          value={metrics?.vendors?.count || 0}
          subtitle={
            metrics?.vendors?.overdue_count > 0 ? `${metrics.vendors.overdue_count} DDAs overdue` : "All DDAs current"
          }
          icon={AlertTriangle}
          borderColor={metrics?.vendors?.overdue_count > 0 ? "warning" : "green"}
          ctaText="View vendor register"
          ctaLink="/compliance/vendors"
        />
      </div>

      {/* Maturity Assessment & Upcoming Deadlines - Side by side per spec 4.4 & 4.5 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Maturity Assessment Panel - Left (2 columns) */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-4">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-base font-semibold mb-4">Maturity assessment — TR²UST framework</CardTitle>
              </div>
              {maturity_assessment?.overall_score > 0 && (
                <Link
                  to={`/compliance/maturity?client=${client?.id}`}
                  className="text-sm text-emerald-500 hover:text-emerald-400 font-medium"
                >
                  Open full assessment →
                </Link>
              )}
            </div>
          </CardHeader>

          {maturity_assessment?.overall_score > 0 ? (
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                {/* Left: Score & Level */}
                <div>
                  <p className="text-5xl font-bold">{maturity_assessment.overall_score.toFixed(1)}%</p>
                  <p
                    className={`text-sm font-medium mt-2 ${
                      maturity_assessment.overall_level === "Reactive"
                        ? "text-red-500"
                        : maturity_assessment.overall_level === "Foundational"
                          ? "text-blue-500"
                          : maturity_assessment.overall_level === "Provisional"
                            ? "text-amber-500"
                            : "text-emerald-500"
                    }`}
                  >
                    Level{" "}
                    {maturity_assessment.overall_score < 25
                      ? "1"
                      : maturity_assessment.overall_score < 50
                        ? "2"
                        : maturity_assessment.overall_score < 75
                          ? "3"
                          : maturity_assessment.overall_score < 90
                            ? "4"
                            : "5"}
                    : {maturity_assessment.overall_level}
                  </p>
                  {maturity_assessment.risk_statement && (
                    <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                      {maturity_assessment.risk_statement}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-3">
                    {maturity_assessment.controls_assessed}% complete • {maturity_assessment.controls_remaining}{" "}
                    controls remaining
                  </p>
                </div>

                {/* Right: Domain Cards Grid (3x2) per Spec 4.4 */}
                <div className="col-span-2">
                  <div className="grid grid-cols-3 gap-3">
                    {maturity_assessment.domains?.map((domain) => {
                      const getLevelColor = (level) => {
                        switch (level) {
                          case "Reactive":
                            return "text-red-500";
                          case "Foundational":
                            return "text-blue-500";
                          case "Provisional":
                            return "text-amber-500";
                          case "Defined":
                            return "text-emerald-500";
                          case "Optimized":
                            return "text-emerald-500";
                          default:
                            return "text-slate-400";
                        }
                      };

                      return (
                        <div key={domain.code} className="p-4 rounded-lg border bg-slate-900/40 border-slate-700/50">
                          {/* Domain Code */}
                          <p className="text-xs font-medium text-muted-foreground">{domain.code}</p>

                          {/* Domain Score */}
                          <p className="text-2xl font-bold mt-1">{(domain.score || 0).toFixed(1)}%</p>

                          {/* Domain Name */}
                          <p className="text-xs text-muted-foreground mt-1">{domain.name}</p>

                          {/* Level Badge */}
                          <p className={`text-xs font-medium mt-2 ${getLevelColor(domain.level)}`}>
                            {domain.level || "Not started"}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          ) : (
            <CardContent>
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No assessment started yet</p>
                <Button asChild>
                  <Link to="/compliance/maturity/workflow/new">Begin Assessment</Link>
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Upcoming Deadlines - Right (1 column) */}
        <UpcomingDeadlines deadlines={upcoming_deadlines || []} />
      </div>

      {/* Recent Activity - 2-Column Grid (per spec 4.6) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Recent activity</CardTitle>
        </CardHeader>
        <CardContent>
          {recent_activity && recent_activity.length > 0 ? (
            <div className="grid grid-cols-2 gap-6">
              {recent_activity.map((activity, idx) => {
                // Determine dot color based on activity type
                const getDotColor = () => {
                  const type = activity.type?.toLowerCase() || "";
                  if (type === "breach") return "bg-red-500";
                  if (type === "dsr") return "bg-blue-500";
                  if (type === "assessment") return "bg-green-500";
                  if (type === "vendor") return "bg-amber-500";
                  if (type === "ropa") return "bg-blue-500";
                  return "bg-slate-500";
                };

                // Format timestamp
                const formatTime = (timestamp) => {
                  if (!timestamp) return "";
                  const date = new Date(timestamp);
                  const today = new Date();
                  const yesterday = new Date(today);
                  yesterday.setDate(yesterday.getDate() - 1);

                  const isToday = date.toDateString() === today.toDateString();
                  const isYesterday = date.toDateString() === yesterday.toDateString();

                  const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

                  if (isToday) return `Today, ${time}`;
                  if (isYesterday) return `Yesterday, ${time}`;

                  const dayName = date.toLocaleDateString(undefined, { weekday: "short" });
                  return `${dayName}, ${time}`;
                };

                return (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full ${getDotColor()} flex-shrink-0 mt-1.5`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground dark:text-white">{activity.description}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{formatTime(activity.timestamp)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
              <p className="text-muted-foreground">No recent activity</p>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}

// Main Dashboard Page with role-based rendering
export default function DashboardPage() {
  const { user, isSuperAdmin, isDPO } = useAuth();

  // Super admin should see the DPO dashboard (full platform view)
  const showDPODashboard = isDPO || isSuperAdmin || user?.role === "org_owner";

  return <DashboardLayout>{showDPODashboard ? <DPODashboard /> : <ClientDashboard />}</DashboardLayout>;
}
